<template>
    <fieldset
        :class="{ 'selected-item': selection && selection.section === section.key && !selection.field }"
        @click.stop="changeSelection({section: section.key})"
        :disabled="!section.editable"
        class="px-3 py-2">
        <DocumentViewerEnabledExtensionsHint
            v-if="showExtensionHints"
            :level="FormExtensionLevel.SECTION"
            :entity="section" class="d-inline-block mr-2"/>
        <div v-if="section.expandable" @click="collapse = !collapse"
             class="form-section-header">
            {{ sectionLabel }}
            <AppExpandChevron :show="!collapse"/>
        </div>
        <template v-if="!collapse">
            <DocumentViewerLines
                v-if="[FormSystemSections.LINES, FormSystemSections.LANDED_COST_LINES].includes(section.key)"
                :isLandedCost="FormSystemSections.LANDED_COST_LINES === section.key"
                @click.native.stop
                :changeSelection="changeSelection"/>
            <DocumentViewerNotes v-else-if="FormSystemSections.NOTES === section.key"/>
            <RelatedDocuments
                v-else-if="FormSystemSections.RELATED_DOCS === section.key"
                :item="isSettingsEditMode ? null : doc"
                :showHeader="false"
                :settings="section.params"
                showViewAssetsAction
                class="pb-3"/>
            <DocumentAllocations v-else-if="FormSystemSections.ALLOCATIONS === section.key"/>

            <transition-group v-else type="transition" name="flip-list" class="row">
                <template v-for="field in sectionFields">

                    <div v-if="field.renderer && field.renderer.break && field.renderer.break.before"
                         :key="section.key + field.path + 'break-before'"
                         class="col-12">
                    </div>

                    <div :key="section.key + field.path"
                         @click.stop="changeSelection({section: field.section.key, field: field.path})"
                         :class="{ 'selected-item': selection && selection.section === section.key && selection.field === field.path,
                                   [`col-${getFieldCols(field)}`]: true
                         }">
                        <DocumentViewerField :field="field"/>

                        <DocumentViewerComponentExtensions
                            v-if="checkFieldHasComponentTypeExtension(field)"
                            :level="FormExtensionLevel.FIELD"
                            :entity="field"
                            :extensions="extensions"/>
                    </div>

                    <div v-if="field.renderer && field.renderer.break && field.renderer.break.after"
                         :key="section.key + field.path + 'break-after'"
                         class="col-12">
                    </div>
                </template>
            </transition-group>

            <DocumentViewerComponentExtensions
                v-if="checkSectionHasComponentTypeExtension(section)"
                :level="FormExtensionLevel.SECTION"
                :entity="section"
                :extensions="extensions"
                class="row"/>
        </template>
    </fieldset>
</template>

<script>
import AppExpandChevron from '../../../../components/shared/AppExpandChevron'
import {FormSystemSections} from '@/types/document-viewer/section'
import DocumentViewerField from './DocumentViewerField'
import DocumentViewerLines from './lines/DocumentViewerLines'
import DocumentViewerNotes from './notes/DocumentViewerNotes'
import DocumentViewerComponentExtensions from './extensions/component-type/DocumentViewerComponentExtensions'
import {mapGetters} from 'vuex'
import {
    fieldComponentTypeExtensionsList,
    FormExtensionLevel,
    sectionComponentTypeExtensionsList
} from '@/types/document-viewer/extension'
import DocumentViewerEnabledExtensionsHint from './extensions/DocumentViewerEnabledExtensionsHint'
import RelatedDocuments from './related-docs/RelatedDocuments'
import DocumentAllocations from './allocations/DocumentAllocations'
import {AppDocLineTypes} from '@/types/document/lines'
import {evaluateTeamLangPlaceholder} from '@/views/settings/team/lang'
import {AutoCancelableDelayer} from '@/decorators/with-delay'

export default {
    name: 'DocumentViewerSection',
    components: {
        RelatedDocuments,
        DocumentAllocations,
        DocumentViewerEnabledExtensionsHint,
        DocumentViewerComponentExtensions,
        DocumentViewerNotes,
        DocumentViewerLines,
        DocumentViewerField,
        AppExpandChevron
    },
    props: {
        section: {
            type: Object,
            required: true
        },
        selection: {
            type: Object,
            required: false
        },
        changeSelection: {
            type: Function,
            required: false,
            default: () => {
            }
        }
    },
    data() {
        return {
            sectionFields: [],
            collapse: false,
            FormSystemSections,
            FormExtensionLevel,
            delayer: new AutoCancelableDelayer(new WeakMap(), 500)
        }
    },
    computed: {
        ...mapGetters('doc_viewer', [
            'doc',
            'notDeletedLines'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'settings',
            'extensions',
            'isSettingsEditMode',
            'showExtensionHints',
            'checkIsAvailableExtension',
            'fields',
            'visibleFieldsWithStateRulesApplied'
        ]),

        lcLines() {
            return this.notDeletedLines.filter(l => l.type === AppDocLineTypes.LANDED_COST)
        },

        sectionLabel() {
            return evaluateTeamLangPlaceholder(this.section.label)
        }
    },
    methods: {
        checkFieldHasComponentTypeExtension(field) {
            return fieldComponentTypeExtensionsList
                .some(e => this.checkIsAvailableExtension(e.name) &&
                    e.available.fields &&
                    e.available.fields.includes(field.path)
                )
        },

        checkSectionHasComponentTypeExtension(section) {
            return sectionComponentTypeExtensionsList
                .some(e => this.checkIsAvailableExtension(e.name) &&
                    e.available.sections &&
                    e.available.sections.includes(section.key)
                )
        },

        getFieldCols(field) {
            if (this.$breakpoint.mdAndUp) {
                return field.section.cols || 4
            } else if (this.$breakpoint.sm) {
                return Math.max(field.section.cols, 6)
            }
            return 12 // mobile
        },

        onFieldsListOrSettingsChange() {
            if (this.section.expandable) {
                this.collapse = this.section.collapse
            }
            this.sectionFields = this.visibleFieldsWithStateRulesApplied.filter(f => f.section.key === this.section.key)
        }
    },
    mounted() {
        this.$watch(vm => [
            vm.fields,
            vm.visibleFieldsWithStateRulesApplied
        ], () => {
            this.delayer.run(this.onFieldsListOrSettingsChange)
        }, {deep: true, immediate: true})
    }
}
</script>

<style scoped lang="scss">
$border-radius: 3px;
$border: 1px dashed $color-grey-light;

.grid-element-wrap {
  padding: 5px 10px;
}

.vue-grid-item:not(.vue-grid-placeholder) {
  border: $border;
  border-radius: $border-radius;

  &:not(.vue-grid-placeholder).selected-block {
    border: 1px dashed $color-purple-light !important;

  }
}

.preview-mode {
  .vue-grid-item {
    border: none;
  }
}

.form-section-header {
  display: inline-block;
  font-size: 20px;
  text-align: left;
  padding: 8px 0;
  width: fit-content;

  &:hover {
    cursor: pointer;
    color: $color-purple-primary;
  }
}

.selected-item {
  background: $color-purple-soft;
  border-radius: 3px;
}
</style>
